import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Observable } from 'rxjs';
import { DataListDialogComponent } from '../data-list-dialog/data-list-dialog.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { EN_DASH } from '../utils/ag-grid-col-defs';

export interface DetailViewerConfig<T> {
  title: string;
  columns: ColDef<T>[];
  loadData: () => Observable<T[]>;
}
export interface HoverTooltipCellRendererParams<TData = any>
  extends ICellRendererParams<TData> {
  tooltipMessage?: string | ((params: ICellRendererParams<TData>) => string);
  tooltipDisabled?: boolean | ((params: ICellRendererParams<TData>) => boolean);
  tooltipClass?: string;
  tooltipPosition?: 'above' | 'below' | 'left' | 'right' | 'before' | 'after';
  tooltipShowDelay?: number;
  tooltipHideDelay?: number;
  displayValueGetter?: (params: ICellRendererParams<TData>) => string;

  detailViewer?: DetailViewerConfig<any>;
}

@Component({
  selector: 'lib-hover-tooltip-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, IconButtonComponent],
  templateUrl: './hover-tooltip-cell-renderer.component.html',
  styleUrls: ['./hover-tooltip-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoverTooltipCellRendererComponent<TData = any>
  implements ICellRendererAngularComp
{
  private readonly dialog = inject(MatDialog);
  // Writable signal representing current AG Grid params
  readonly params = signal<HoverTooltipCellRendererParams<TData> | null>(null);

  readonly displayValue = computed((): string => {
    const p = this.params();

    if (!p) {
      return '';
    }

    if (p.displayValueGetter) {
      return p.displayValueGetter(p);
    }

    const value = p.valueFormatted ?? p.value;

    return value === null || value === undefined ? '' : String(value);
  });

  readonly tooltipMessage = computed((): string => {
    const p = this.params();

    if (!p) {
      return '';
    }

    if (typeof p.tooltipMessage === 'function') {
      return p.tooltipMessage(p);
    }

    return p.tooltipMessage ?? '';
  });

  readonly tooltipDisabled = computed((): boolean => {
    const p = this.params();

    if (!p) {
      return true;
    }

    if (typeof p.tooltipDisabled === 'function') {
      return p.tooltipDisabled(p);
    }

    return p.tooltipDisabled ?? !this.tooltipMessage();
  });

  readonly tooltipClass = computed(
    (): string => this.params()?.tooltipClass ?? 'hover-tooltip'
  );

  readonly tooltipPosition = computed(
    (): 'above' | 'below' | 'left' | 'right' | 'before' | 'after' =>
      this.params()?.tooltipPosition ?? 'below'
  );

  readonly tooltipShowDelay = computed(
    (): number => this.params()?.tooltipShowDelay ?? 0
  );

  readonly tooltipHideDelay = computed(
    (): number => this.params()?.tooltipHideDelay ?? 200
  );

  readonly detailViewerConfig = computed(
    (): DetailViewerConfig<any> | undefined => this.params()?.detailViewer
  );

  readonly showEyeIcon = computed(() => {
    const value = this.displayValue()?.trim();
    const hasValidValue = !!value && value !== EN_DASH;
    return hasValidValue && !!this.detailViewerConfig();
  });

  agInit(params: HoverTooltipCellRendererParams<TData>): void {
    this.params.set(params);
  }

  refresh(params: HoverTooltipCellRendererParams<TData>): boolean {
    this.params.set(params);
    return true;
  }

  openDetails(event: MouseEvent): void {
    event.stopPropagation();

    const config = this.detailViewerConfig();

    if (!config) {
      return;
    }

    this.dialog.open(DataListDialogComponent, {
      panelClass: 'details-list-dialog',
      width: '800px',
      height: '840px',
      disableClose: true,
      data: config,
    });
  }
}
